import React, { useState, useEffect } from "react";
import { LogOutIcon } from "lucide-react";
import md5 from "blueimp-md5";
import { Link } from "react-router-dom";
import { logout as logoutApi } from "../utils/api";
import { useNavigate } from "react-router-dom";

export default function CaHeader({
  toggleSidebar,
  isMobile,
  fullName: propFullName,
  email: propEmail,
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();

  const fullName =
    (propEmail || localStorage.getItem("email") || "").split("@")[0];

  const email =
    propEmail || localStorage.getItem("email") || "";

  const normalizedEmail = email.trim().toLowerCase();
  const hash = normalizedEmail ? md5(normalizedEmail) : null;
  const avatarUrl = hash
  ? `https://www.gravatar.com/avatar/${hash}?s=80&d=identicon`
  : "/avatar.png";

  // preload avatar (optional)
  useEffect(() => {
    if (hash) {
      const img = new Image();
      img.src = avatarUrl;
    }
  }, [avatarUrl, hash]);

  const handleLogout = async () => {
    try {
      await logoutApi();
      localStorage.clear();
      navigate("/");
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showDropdown) return;
    const handleClickOutside = (event) => {
      // Check if click is outside the dropdown and button
      const dropdown = document.getElementById("admin-profile-dropdown");
      const button = document.getElementById("admin-profile-button");
      if (
        dropdown &&
        !dropdown.contains(event.target) &&
        button &&
        !button.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown]);

  return (
    <div className="bg-white p-4 sticky top-0 z-10 flex items-center justify-between border-b">
      {/* Left – hamburger + search */}
      <div className="flex items-center flex-1 mr-4">
        <button
          onClick={toggleSidebar}
          className="text-gray-700 md:hidden focus:outline-none mr-2"
        >
          ☰
        </button>
        <div className="relative w-full max-w-xs md:w-80">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            type="search"
            className="bg-gray-50 border border-gray-100 text-gray-900 text-sm rounded-lg block w-full pl-10 p-2.5 focus:outline-none"
            placeholder="Search"
          />
        </div>
      </div>

      {/* Right – notifications + profile */}
      <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
        {/* Notification bell */}
        <div className="relative">
          <button className="text-blue-500 relative focus:outline-none">
            <svg
              className="w-5 h-5 md:w-6 md:h-6"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
            </svg>
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
              2
            </span>
          </button>
        </div>

        {/* Profile dropdown */}
        <div className="relative flex items-center gap-1 md:gap-2">
          <button
            id="admin-profile-button"
            onClick={() => setShowDropdown((prev) => !prev)}
            className="flex items-center gap-1 md:gap-2 focus:outline-none"
            type="button"
          >
            <div className="h-6 w-6 md:h-8 md:w-8 rounded-full overflow-hidden flex-shrink-0">
              <img
                src={avatarUrl}
                alt="Profile"
                className="h-full w-full object-cover"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/avatar.png";
                }}
              />
            </div>
            <div className="hidden sm:block text-left">
              <div className="text-xs md:text-sm font-medium">{fullName}</div>
              <div className="text-xs text-gray-500">Admin</div>
            </div>
            <svg
              className="w-3 h-3 md:w-4 md:h-4 text-gray-500 hidden sm:block"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {showDropdown && (
            <div
              id="admin-profile-dropdown"
              className="absolute right-0 w-32 lg:w-40 bg-white border rounded shadow-lg z-20 mt-28"
            >
              <Link to="/">
                <div className="px-4 py-2 text-sm text-gray-800 hover:bg-gray-100">Home</div>
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full text-left px-4 py-2 text-xs lg:text-md font-medium text-red-500 hover:bg-gray-100"
              >
                <LogOutIcon size={16} /> Log Out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
